import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

type VariantStyles = {
  backgroundColor: string;
  borderColor?: string;
  color: string;
};

const createVariantStyles = ({ backgroundColor, borderColor, color }: VariantStyles) => css`
  background-color: ${backgroundColor};
  color: ${color};

  ${borderColor
    ? css`
        border: 1px solid ${borderColor};
      `
    : css`
        border: none;
      `}

  &:visited {
    background-color: ${backgroundColor};
    color: ${color};
    ${borderColor
      ? css`
          border-color: ${borderColor};
        `
      : ''}
  }

  &:hover:not([disabled]) {
    background-color: color-mix(
      in srgb,
      ${color} var(${themeVariables.tint.hover}),
      ${backgroundColor} calc(100% - var(${themeVariables.tint.hover}))
    );
  }

  &[disabled] {
    background-color: color-mix(
      in srgb,
      ${color} var(${themeVariables.tint.disabled}),
      ${backgroundColor} calc(100% - var(${themeVariables.tint.disabled}))
    );
    color: ${color};
  }
`;

export const root = css`
  appearance: none;
  border-radius: var(${themeVariables.borderRadius.full.mobile});
  cursor: pointer;
  font-family: var(${themeVariables.typography.body.fontFamily}, sans-serif);
  font-size: var(${themeVariables.typography.body.sizes.sm.fontSize});
  font-weight: 700;
  line-height: 1;
  padding: var(${themeVariables.spacing.sm}) var(${themeVariables.spacing.lg});
  text-decoration: none;

  &[disabled],
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`;

export const primary = css`
  ${createVariantStyles({
    backgroundColor: `var(${themeVariables.colors.surface.brand})`,
    color: `var(${themeVariables.colors.surface.default})`,
  })}
`;

export const primaryInverted = css`
  ${createVariantStyles({
    backgroundColor: `var(${themeVariables.colors.surface.default})`,
    color: `var(${themeVariables.colors.surface.brand})`,
  })}
`;

export const secondaryDefault = css`
  ${createVariantStyles({
    backgroundColor: `var(${themeVariables.colors.surface.default})`,
    borderColor: `var(${themeVariables.colors.content.brand})`,
    color: `var(${themeVariables.colors.content.brand})`,
  })}
`;

export const secondaryInverted = css`
  ${createVariantStyles({
    backgroundColor: 'transparent',
    borderColor: `var(${themeVariables.colors.surface.default})`,
    color: `var(${themeVariables.colors.surface.default})`,
  })}
`;

export const getVariantStyles = ({
  inverted = false,
  secondary = false,
}: {
  inverted?: boolean;
  secondary?: boolean;
}) => {
  if (secondary) {
    return inverted ? secondaryInverted : secondaryDefault;
  }

  return inverted ? primaryInverted : primary;
};

export const compact = css`
  padding: var(${themeVariables.spacing.xs}) var(${themeVariables.spacing.md});
`;
